import React, { useState } from 'react';
import { Pencil } from 'lucide-react';
import { BundleBadge, BundleContents } from './BundleLineDetails';
import { MEMBER_DISCOUNT_RATE, STANDARD_SHIPPING_AMOUNT } from '../../hooks/useOneTimeOrderPricing';
import type { OneTimeOrderPricing, OneTimeOrderShippingOption } from '../../hooks/useOneTimeOrderPricing';
import type { OneTimeOrderLine } from './OneTimeOrderModal';
interface OneTimeOrderReviewProps {
  lines: OneTimeOrderLine[];
  pricing: OneTimeOrderPricing;
  shippingAddressLines: string[];
  deliveryMethod: string;
  paymentMethodLabel: string;
}
const formatMoney = (value: number) => `$${value.toFixed(2)}`;
const formatSigned = (value: number) => value < 0 ? `-${formatMoney(Math.abs(value))}` : `+${formatMoney(value)}`;
const inputClass = 'h-9 w-full pl-7 pr-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent';
export function OneTimeOrderReview({
  lines,
  pricing,
  shippingAddressLines,
  deliveryMethod,
  paymentMethodLabel
}: OneTimeOrderReviewProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftPrice, setDraftPrice] = useState('');
  const discountPercent = Math.round(MEMBER_DISCOUNT_RATE * 100);
  const startEditing = (id: string, currentPrice: number) => {
    setEditingId(id);
    setDraftPrice(currentPrice.toFixed(2));
  };
  const commitEditing = () => {
    if (!editingId) return;
    const parsed = parseFloat(draftPrice);
    if (Number.isFinite(parsed) && parsed >= 0) pricing.setCustomPrice(editingId, Math.round(parsed * 100) / 100);
    setEditingId(null);
  };
  return <div className="space-y-5" data-id="element-4961" data-mp="2ubp2dkv7wv4h">
      <div data-id="element-4962" data-mp="2ucgmnzjd76fg">
        <h3 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-4963" data-mp="2ud04zhcxfwu0">
          Review and pay
        </h3>
        <p className="text-[13px] text-[#64748B] mt-1" data-id="element-4964" data-mp="xgj4krxdu4ry">
          Check the order before charging. The subscription itself is not
          changed.
        </p>
      </div>

      {/* Line items */}
      <div className="border border-[#E2E8F0] rounded-md overflow-hidden" data-id="element-4965" data-mp="2mxo6xnkrblea">
        <table className="w-full" data-id="element-4966" data-mp="2rs24nwmw64m4">
          <thead className="bg-[#F1F5F9]" data-id="element-4967" data-mp="2rsaflizgezuo">
            <tr data-id="element-4968" data-mp="2d2mhsrqhejbp">
              <th className="px-4 py-2.5 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-4969" data-mp="2d35yy68b53oo">
                Product
              </th>
              <th className="px-4 py-2.5 text-center text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-4970" data-mp="35pqt1eg63tvx">
                Qty
              </th>
              <th className="px-4 py-2.5 text-right text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-4971" data-mp="3amzafcthrtw4">
                Unit price
              </th>
              <th className="px-4 py-2.5 text-right text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-4972" data-mp="30pup1f7h02gd">
                Total
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]" data-id="element-4973" data-mp="1pcs467yc8qqe">
            {lines.map(line => {
            const result = pricing.getLine(line);
            const isEditing = editingId === line.id;
            const showOriginal = result.unitPrice !== result.basePrice;
            return <tr key={line.id} data-id="element-4974" data-mp="1rr6znxh5jfx8">
                  <td className="px-4 py-3 align-top" data-id="element-4975" data-mp="1rq5h77xt3r5h">
                    <p className="text-[14px] text-[#0F172A] flex items-center gap-2" data-id="element-4976" data-mp="1mr180havttf5">
                      {line.name}
                      {line.bundleContents && <BundleBadge data-id="element-4977" data-mp="z29kw4c5g198" />}
                    </p>
                    {line.bundleContents && <BundleContents contents={line.bundleContents} data-id="element-4978" data-mp="u4809ez2rtvn" />}
                    {result.isCustom && <div className="flex items-center gap-2 mt-1" data-id="element-4979" data-mp="18suq4ambw414">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#FEF3C7] text-[#92400E]" data-id="element-4980" data-mp="141codkchdzf3">
                          Custom price
                        </span>
                        <button type="button" onClick={() => pricing.resetCustomPrice(line.id)} className="text-[12px] font-medium text-[#0EA5E9] hover:text-[#0284C7]" data-id="element-4981" data-mp="bd74ezt94egb">
                          Reset
                        </button>
                      </div>}
                  </td>
                  <td className="px-4 py-3 text-center align-top text-[14px] text-[#0F172A]" data-id="element-4982" data-mp="3jrcu88256uni">
                    {line.quantity}
                  </td>
                  <td className="px-4 py-3 text-right align-top" data-id="element-4983" data-mp="3oj4c7u78fs21">
                    {isEditing ? <div className="relative inline-block w-[110px]" data-id="element-4984" data-mp="3tg4hi2w9cqs3">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-[#64748B]" data-id="element-4985" data-mp="2a3d2d6ssvff">$</span>
                        <input type="number" min="0" step="0.01" autoFocus value={draftPrice} onChange={e => setDraftPrice(e.target.value)} onBlur={commitEditing} onKeyDown={e => {
                    if (e.key === 'Enter') commitEditing();
                    if (e.key === 'Escape') setEditingId(null);
                  }} aria-label={`Unit price for ${line.name}`} className={`${inputClass} text-right`} data-id="element-4986" data-mp="6d9kho5ncsnh" />
                      </div> : <button type="button" onClick={() => startEditing(line.id, result.unitPrice)} aria-label={`Edit unit price for ${line.name}`} className="group inline-flex flex-col items-end cursor-pointer" data-id="element-4987" data-mp="25kpy6rf1bkqq">
                        <span className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#0F172A]" data-id="element-4988" data-mp="1vtlmc0knog04">
                          <Pencil className="w-3 h-3 text-[#94A3B8] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-4989" data-mp="1qsec7uv1j4qa" />
                          {formatMoney(result.unitPrice)}
                        </span>
                        {showOriginal && <span className="text-[12px] text-[#94A3B8] line-through" data-id="element-4990" data-mp="2eja936ecsq4e">
                            {formatMoney(result.basePrice)}
                          </span>}
                      </button>}
                  </td>
                  <td className="px-4 py-3 text-right align-top text-[14px] font-medium text-[#0F172A]" data-id="element-4991" data-mp="135fw4yau1ysw">
                    {formatMoney(result.lineTotal)}
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-5" data-id="element-4992" data-mp="15j1l9nylo0i4">
        {/* Pricing controls */}
        <section className="space-y-4" aria-labelledby="oto-pricing-heading" data-id="element-4993" data-mp="qmasvyncfp20">
          <h4 id="oto-pricing-heading" className="text-[14px] font-semibold text-[#475569] uppercase tracking-[0.5px]" data-id="element-4994" data-mp="qoeug9xrsbz4">
            Pricing
          </h4>

          <div className="flex items-center justify-between gap-4" data-id="element-4995" data-mp="hsyxhk1pxgpb">
            <span id="oto-member-discount-label" className="text-[14px] font-medium text-[#0F172A]" data-id="element-4996" data-mp="mkz41vcan7uv">
              Apply member discount ({discountPercent}%)
            </span>
            <button type="button" role="switch" aria-checked={pricing.memberDiscountOn} aria-labelledby="oto-member-discount-label" onClick={() => pricing.setMemberDiscountOn(!pricing.memberDiscountOn)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${pricing.memberDiscountOn ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-4997" data-mp="cunmv73ln261">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${pricing.memberDiscountOn ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-4998" data-mp="3bfija4hh3eez" />
            </button>
          </div>

          <div data-id="element-4999" data-mp="3kedszqjq76n1">
            <label htmlFor="oto-shipping" className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-5000" data-mp="3kdu944aim86d">
              Shipping
            </label>
            <div className="flex flex-wrap items-center gap-3" data-id="element-5001" data-mp="3uduwq2ff3agd">
              <select id="oto-shipping" value={pricing.shippingOption} onChange={e => pricing.setShippingOption(e.target.value as OneTimeOrderShippingOption)} className="h-9 px-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-5002" data-mp="3ubif5qdkn23u">
                <option value="standard" data-id="element-5003" data-mp="gyyjcqf9ez2f">Standard (subscription delivery method)</option>
                <option value="free" data-id="element-5004" data-mp="1ob5sl5psvxbo">Free shipping</option>
                <option value="custom" data-id="element-5005" data-mp="1oamb1q42bay6">Custom amount</option>
              </select>
              {pricing.shippingOption === 'custom' && <div className="relative w-[120px]" data-id="element-5006" data-mp="1seby2b539kh3">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-[#64748B]" data-id="element-5007" data-mp="1xcvl6evf52b1">$</span>
                  <input type="number" min="0" step="0.01" value={pricing.customShipping} onChange={e => pricing.setCustomShipping(e.target.value)} placeholder="0.00" aria-label="Custom shipping amount" className={inputClass} data-id="element-5008" data-mp="22a573kjlmvu4" />
                </div>}
            </div>
            {pricing.shippingOption === 'standard' && <p className="text-[12px] text-[#64748B] mt-1.5" data-id="element-5009" data-mp="1ejrmgf3cfz9k">
                {deliveryMethod} · {formatMoney(STANDARD_SHIPPING_AMOUNT)}
              </p>}
          </div>

          {pricing.hasChanges && <div data-id="element-5010" data-mp="lvvqxdpigded">
              <label htmlFor="oto-reason" className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-5011" data-mp="luavzmzaasfz">
                Reason for price change{' '}
                <span className="font-normal text-[#94A3B8]" data-id="element-5012" data-mp="jaxsjyj1cv94">(optional)</span>
              </label>
              <input id="oto-reason" type="text" value={pricing.reason} onChange={e => pricing.setReason(e.target.value)} placeholder="e.g. Cellar door 6-pack offer" className="h-9 w-full px-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-5013" data-mp="edxmvoq3luij" />
            </div>}
        </section>

        {/* Totals */}
        <div className="space-y-3 self-start" data-id="element-5014" data-mp="3rqhhwmgl5tiw">
          <aside className="bg-[#F8FAFC] rounded-md border border-[#E2E8F0] p-4 space-y-2" aria-label="Order totals" data-id="element-5015" data-mp="3ms68gcu7dl17">
            <div className="flex items-center justify-between" data-id="element-5016" data-mp="3hunycj4g8zrm">
              <span className="text-[14px] text-[#475569]" data-id="element-5017" data-mp="3cuiale1avxet">Item total</span>
              <span className="text-[14px] text-[#0F172A] font-medium" data-id="element-5018" data-mp="3cwmdbsngqqdi">
                {formatMoney(pricing.itemTotal)}
              </span>
            </div>
            {pricing.hasOrderTotal ? <div className="flex items-center justify-between" data-id="element-5019" data-mp="3t4j60ofrk6xk">
                <span className="text-[14px] text-[#475569]" data-id="element-5020" data-mp="3o82fjnl3dghb">
                  Order adjustment
                </span>
                <span className={`text-[14px] font-medium ${pricing.orderAdjustment < 0 ? 'text-[#10B981]' : 'text-[#0F172A]'}`} data-id="element-5021" data-mp="6phqbgp9rncb">
                  {formatSigned(pricing.orderAdjustment)}
                </span>
              </div> : pricing.memberDiscount > 0 && <div className="flex items-center justify-between" data-id="element-5022" data-mp="358evli2bhyl4">
                  <span className="text-[14px] text-[#10B981]" data-id="element-5023" data-mp="35a9i30t8lyrb">
                    Member discount ({discountPercent}%)
                  </span>
                  <span className="text-[14px] text-[#10B981] font-medium" data-id="element-5024" data-mp="3f62zaxhwigff">
                    -{formatMoney(pricing.memberDiscount)}
                  </span>
                </div>}
            <div className="flex items-center justify-between" data-id="element-5025" data-mp="ugdl8mp0uyns">
              <span className="text-[14px] text-[#475569]" data-id="element-5026" data-mp="ug3v8z3z8ri3">Shipping</span>
              <span className="text-[14px] text-[#0F172A] font-medium" data-id="element-5027" data-mp="11yuyn400qxm1">
                {pricing.shipping === 0 ? 'Free' : formatMoney(pricing.shipping)}
              </span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-[#E2E8F0]" data-id="element-5028" data-mp="s2i4k50kahqr">
              <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-5029" data-mp="x008ti3mw8sf">
                Total (approx.)
              </span>
              <span className="text-[16px] font-bold text-[#0EA5E9]" data-id="element-5030" data-mp="1kqw810af669p">
                {formatMoney(pricing.total)}
              </span>
            </div>
            <p className="text-[12px] text-[#64748B] pt-1" data-id="element-5031" data-mp="1b0uf9v74fzog">
              Tax is calculated by Shopify when the card is charged.
            </p>
          </aside>

          {pricing.isOrderTotalOpen ? <div data-id="element-5032" data-mp="23p8ege9y521l">
              <label htmlFor="oto-order-total" className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-5033" data-mp="23oqfh9vwg9pz">
                Order total
              </label>
              <div className="flex items-center gap-3" data-id="element-5034" data-mp="28ht91i0ofj66">
                <div className="relative flex-1" data-id="element-5035" data-mp="2ianp8hj8jje0">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-[#64748B]" data-id="element-5036" data-mp="2dcujtqprates">$</span>
                  <input id="oto-order-total" type="number" min="0" step="0.01" autoFocus value={pricing.orderTotal} onChange={e => pricing.setOrderTotal(e.target.value)} placeholder="0.00" className={inputClass} data-id="element-5037" data-mp="29b0o7wuf4flh" />
                </div>
                <button type="button" onClick={pricing.removeOrderTotal} className="text-[13px] font-medium text-[#0EA5E9] hover:text-[#0284C7]" data-id="element-5038" data-mp="1zduwqnhw6mkq">
                  Remove
                </button>
              </div>
              <p className="text-[12px] text-[#64748B] mt-1.5" data-id="element-5039" data-mp="1pnta8056tc7x">
                Replaces the item prices for the whole order, before shipping.
              </p>
            </div> : <button type="button" onClick={pricing.openOrderTotal} className="text-[13px] font-medium text-[#0EA5E9] hover:text-[#0284C7]" data-id="element-5040" data-mp="1gmv1x5npuijj">
              Set order total
            </button>}
        </div>
      </div>

      {/* Fulfilment details */}
      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-5041" data-mp="3ncyc5zvuvyh2">
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-3" data-id="element-5042" data-mp="3s8vq5zjncsqo">
          <dt className="text-[12px] font-medium text-[#475569] mb-1" data-id="element-5043" data-mp="134359161w83">Ship to</dt>
          <dd className="text-[13px] text-[#0F172A] leading-relaxed" data-id="element-5044" data-mp="54z6ej2nqjoi">
            {shippingAddressLines.map((addressLine, index) => <div key={index} data-id="element-5045" data-mp="34l5l4b0cknbz" data-mpi={index}>{addressLine}</div>)}
          </dd>
        </div>
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-3" data-id="element-5046" data-mp="39j7djmjnxz04">
          <dt className="text-[12px] font-medium text-[#475569] mb-1" data-id="element-5047" data-mp="39h2zibs1vjm4">
            Delivery method
          </dt>
          <dd className="text-[13px] text-[#0F172A]" data-id="element-5048" data-mp="3ibr5631jf3bg">{deliveryMethod}</dd>
        </div>
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-3" data-id="element-5049" data-mp="3idlt7q83vbjr">
          <dt className="text-[12px] font-medium text-[#475569] mb-1" data-id="element-5050" data-mp="2ltmiw6e5ddb0">
            Payment method
          </dt>
          <dd className="text-[13px] text-[#0F172A]" data-id="element-5051" data-mp="2ptdimzlnj3ro">{paymentMethodLabel}</dd>
        </div>
      </dl>
    </div>;
}